import { redirectToDoorLogin } from './entryHost';
import { IDLE_TIMEOUT_MS, shouldResetIdleClock } from './idleClock';

export { IDLE_TIMEOUT_MS, shouldResetIdleClock };

/**
 * Cierre de sesión por inactividad (compartido shell / Food / Training).
 * 1 hora sin interacción → limpia tokens y redirige al login de la puerta.
 */
const ACTIVITY_KEY = 'd28d_last_activity_at';
const CHECK_EVERY_MS = 30 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'visibilitychange'];

function now() {
  return Date.now();
}

export function touchSessionActivity() {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now()));
  } catch { /* noop */ }
}

export function getLastActivityAt() {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

export function clearSessionActivity() {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch { /* noop */ }
}

function hasAnyModuleSession() {
  try {
    if (localStorage.getItem('d28d_token')) return true;
    const legacy = localStorage.getItem('token');
    if (legacy) return true;
    if (localStorage.getItem('food_access_token')) return true;
    if (localStorage.getItem('food_refresh_token')) return true;
    if (localStorage.getItem('training_access_token')) return true;
    if (localStorage.getItem('training_refresh_token')) return true;
    if (localStorage.getItem('accessToken')) return true;
    if (localStorage.getItem('refreshToken')) return true;
  } catch { /* noop */ }
  return false;
}

/** Limpia tokens shell + Food embebido (+ claves training si existen). */
export function clearAllModuleSessions() {
  const keys = [
    'd28d_token',
    'token',
    'refreshToken',
    'accessToken',
    'food_access_token',
    'food_refresh_token',
    'food_user',
    'food_subscription',
    'training_access_token',
    'training_refresh_token',
    'user',
    'subscription',
  ];
  for (const k of keys) {
    try { localStorage.removeItem(k); } catch { /* noop */ }
  }
  try { sessionStorage.removeItem('futbol_sso_sent'); } catch { /* noop */ }
  clearSessionActivity();
}

function entrySsoInFlight() {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('entry_sso');
  } catch {
    return false;
  }
}

/**
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs]
 * @param {() => boolean} [opts.isActive] — si hay sesión que vigilar
 * @param {() => void} [opts.onIdle] — callback antes del redirect (opcional)
 * @returns {() => void} cleanup
 */
export function startIdleSessionWatcher(opts = {}) {
  const timeoutMs = Number(opts.timeoutMs) > 0 ? Number(opts.timeoutMs) : IDLE_TIMEOUT_MS;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : hasAnyModuleSession;
  const onIdle = typeof opts.onIdle === 'function' ? opts.onIdle : null;
  let idleFired = false;
  let lastTouchWrite = 0;

  const markActivity = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (!isActive()) return;
    const t = now();
    // Throttle writes (evita spam en mousemove)
    if (t - lastTouchWrite < 5000) return;
    lastTouchWrite = t;
    touchSessionActivity();
  };

  const expire = () => {
    if (idleFired) return;
    if (entrySsoInFlight()) {
      touchSessionActivity();
      return;
    }
    idleFired = true;
    try { onIdle?.(); } catch { /* noop */ }
    clearAllModuleSessions();
    try {
      sessionStorage.setItem('shell_idle_logout', '1');
    } catch { /* noop */ }
    if (typeof window !== 'undefined') {
      redirectToDoorLogin({ idle: true });
    }
  };

  const check = () => {
    if (!isActive()) return;
    const last = getLastActivityAt();
    if (last == null) {
      touchSessionActivity();
      return;
    }
    if (now() - last >= timeoutMs) expire();
  };

  if (isActive() && shouldResetIdleClock(getLastActivityAt(), now(), timeoutMs)) {
    touchSessionActivity();
  }

  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, markActivity, { passive: true });
  }
  const intervalId = window.setInterval(check, CHECK_EVERY_MS);
  // Otras pestañas: ACTIVITY_KEY se borra en logout intencional y en idle.
  // No marcar idle aquí (evita anclar el aviso en login tras cerrar sesión normal).
  const onStorage = (e) => {
    if (e.key === ACTIVITY_KEY && e.newValue == null && isActive()) {
      if (idleFired) return;
      idleFired = true;
      try { onIdle?.(); } catch { /* noop */ }
      clearAllModuleSessions();
      if (typeof window !== 'undefined' && !/\/login\/?$/.test(window.location.pathname)) {
        redirectToDoorLogin();
      }
    }
  };
  window.addEventListener('storage', onStorage);
  check();

  return () => {
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, markActivity);
    }
    window.removeEventListener('storage', onStorage);
    window.clearInterval(intervalId);
  };
}
